import { useEffect, useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import { LoadingState, TableRowActions, editIcon, viewIcon } from '@tpct/admin-ui';
import type { TableRowAction } from '@tpct/admin-ui';
import CyclesTable from '@tpct/mission-core/components/CyclesTable';
import type { CycleRowData, CycleStatsData } from '@tpct/mission-core/components/CyclesTable';
import { buildVSMEFormUrl } from '../../../../companies/src/utils/vsmeApi';
import { fetchWithNonce } from '../../utils/fetchWithNonce';
import { parseApiError } from '../../utils/parseApiError';
import { errorToUserFacingMessage } from '../../api/apiErrors';
import { vsmeDossierRelativePath } from '@tpct/mission-core/utils/vsmePortalRoutes';

type DiligenceBootstrapPayload = {
  cyclesData?: Record<string, {
    name: string;
    stats: CycleStatsData;
    form_completion_percentage?: number;
    first_step_id?: string | null;
  }>;
};

type MissionCyclesTableProps = {
  vsmeBaseUrl: string;
  nonce: string;
  linkedReportId: number | null;
  missionId: number;
  companyId: number;
  onNavigate: (path: string) => void;
};

const MissionCyclesTable = ({
  vsmeBaseUrl,
  nonce,
  linkedReportId,
  missionId,
  companyId,
  onNavigate,
}: MissionCyclesTableProps) => {
  const [cycles, setCycles] = useState<CycleRowData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (linkedReportId === null) {
      setCycles([]);
      setError('');
      return;
    }

    let active = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchWithNonce(`${vsmeBaseUrl}reports/${linkedReportId}/diligences/bootstrap`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-WP-Nonce': nonce,
          },
        });

        if (!response.ok) {
          if (active) {
            setError(await parseApiError(response));
          }
          return;
        }

        const raw = (await response.json()) as { success?: boolean; data?: DiligenceBootstrapPayload };
        const payload = raw.data ?? {};
        const cyclesData = payload.cyclesData ?? {};
        const rows: CycleRowData[] = Object.entries(cyclesData).map(([num, data]) => ({
          num: Number.parseInt(num, 10),
          name: data.name,
          stats: data.stats,
          formCompletionPercentage: data.form_completion_percentage ?? 0,
          firstStepId: data.first_step_id ?? null,
        }));

        if (active) {
          setCycles(rows.sort((a, b) => a.num - b.num));
        }
      } catch (e) {
        if (active) {
          setError(errorToUserFacingMessage(e, __('Impossible de charger les cycles de diligence.')));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => {
      active = false;
    };
  }, [linkedReportId, nonce, vsmeBaseUrl]);

  return (
    <section className="container-style mission-page__card mission-page__card--span-5" aria-labelledby="mission-cycles-heading">
      <h2 id="mission-cycles-heading" className="h4">{__('Cycles de la mission')}</h2>

      {linkedReportId === null ? (
        <p className="mission-page__empty">{__('Aucun dossier de travail lié. Démarrez le formulaire VS pour accéder aux cycles.')}</p>
      ) : null}

      {linkedReportId !== null && loading ? (
        <LoadingState
          columns={[
            __('Cycle'),
            __('Complétion formulaire'),
            __('Diligences'),
            __('Actions'),
          ]}
          rowCount={4}
          className="mission-cycles-loading"
        />
      ) : null}
      {error !== '' ? <p className="add-to-list-view__error" role="alert">{error}</p> : null}

      {linkedReportId !== null && !loading && cycles.length > 0 ? (
        <CyclesTable
          cycles={cycles}
          tableClassName="mission-cycles__table"
          renderActions={(cycle) => {
            const actions: TableRowAction[] = [
              {
                key: 'resume-form',
                label: __('Continuer le formulaire'),
                icon: editIcon,
                onClick: () => {
                  window.location.assign(
                    buildVSMEFormUrl(
                      String(linkedReportId),
                      companyId,
                      missionId,
                      cycle.firstStepId ?? null,
                    ),
                  );
                },
              },
              {
                key: 'review-diligences',
                label: __('Contrôler les diligences'),
                icon: viewIcon,
                onClick: () => onNavigate(vsmeDossierRelativePath(missionId, { cycle: cycle.num })),
              },
            ];

            return (
              <TableRowActions
                actions={actions}
                overflowLabel={__('Plus d’actions')}
              />
            );
          }}
        />
      ) : null}
    </section>
  );
};

export default MissionCyclesTable;
